import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

function localDateTimeValue(value = new Date()) {
  const date = new Date(value);
  const pad = (number) => String(number).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * Fast, teacher-first capture for an individual child. The server makes this
 * append-only and requires an administrator to decide whether a family sees
 * it. The sheet intentionally has no score, rating, milestone, or framework.
 */
export default function LearningObservationSheet({
  open,
  childName,
  focuses = [],
  saving = false,
  onOpenChange,
  onSubmit,
  restoreFocusRef,
}) {
  const [focusId, setFocusId] = useState("");
  const [note, setNote] = useState("");
  const [observedAt, setObservedAt] = useState(localDateTimeValue());
  const characterCount = note.length;

  const activeFocuses = useMemo(
    () => focuses.filter((focus) => focus.isActive !== false),
    [focuses],
  );

  useEffect(() => {
    if (!open) return;
    setFocusId("");
    setNote("");
    setObservedAt(localDateTimeValue());
  }, [open]);

  const submit = (event) => {
    event.preventDefault();
    const nextNote = note.trim();
    if (!nextNote || saving) return;
    const parsedTime = new Date(observedAt);
    onSubmit({
      focusId: focusId || null,
      note: nextNote,
      observedAt: Number.isNaN(parsedTime.getTime()) ? new Date().toISOString() : parsedTime.toISOString(),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="learning-observation-sheet"
        className="max-w-md rounded-2xl border-neutral-200 bg-white p-0"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          restoreFocusRef?.current?.focus?.({ preventScroll: true });
        }}
      >
        <DialogHeader className="border-b border-neutral-100 px-6 pb-4 pt-5">
          <DialogTitle className="font-heading text-xl font-semibold tracking-tight text-neutral-900">
            Notice a moment
          </DialogTitle>
          <DialogDescription className="mt-1 text-sm leading-6 text-neutral-600">
            Record what you noticed about {childName || "this child"}. An administrator reviews it before a family can see it.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5 px-6 py-5">
          <label className="block">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
              Focus area <span className="normal-case tracking-normal">(optional)</span>
            </span>
            <select
              data-testid="learning-observation-focus"
              value={focusId}
              onChange={(event) => setFocusId(event.target.value)}
              className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm text-neutral-900 shadow-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
            >
              <option value="">No focus area</option>
              {activeFocuses.map((focus) => (
                <option key={focus.id} value={focus.id}>
                  {focus.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
              What did you notice?
            </span>
            <textarea
              required
              data-testid="learning-observation-note"
              rows={4}
              maxLength={2000}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="A short, specific moment from the day…"
              className="w-full resize-y rounded-xl border border-neutral-200 bg-white px-4 py-3 text-sm leading-6 text-neutral-900 shadow-sm outline-none transition placeholder:text-neutral-400 focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
            <span className="mt-1 block text-right text-xs text-neutral-400" aria-live="polite">
              {characterCount}/2000
            </span>
          </label>

          <details className="rounded-xl bg-neutral-50 px-3 py-2.5">
            <summary className="cursor-pointer text-sm font-medium text-neutral-700">
              Change observed time
            </summary>
            <label className="mt-3 block">
              <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
                Observed time
              </span>
              <input
                data-testid="learning-observation-time"
                type="datetime-local"
                value={observedAt}
                onChange={(event) => setObservedAt(event.target.value)}
                className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm text-neutral-900 shadow-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </label>
          </details>

          <div className="flex items-center justify-end gap-3 pt-1">
            <button
              type="button"
              data-testid="learning-observation-cancel"
              disabled={saving}
              onClick={() => onOpenChange(false)}
              className="rounded-full px-5 py-2.5 text-sm font-medium text-neutral-600 transition hover:bg-neutral-100 disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="learning-observation-submit"
              disabled={saving || !note.trim()}
              className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save for review"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
